// 🔧 vote_defaults.js
// ✅ Purpose: Creates canonical Firestore vote documents for the LGV Seed Engine
// ✅ Features:
// - Preserves existing seeded standard-vote document behavior
// - Includes legacy/current vote fields expected by LGV rules and Event Home
// - Creates canonical Points / Detailed Scorecard vote documents
// - Persists totalPoints and maxBallotPoints for judged scoring
// - Persists scores[] with factor metadata
// - Persists checkedHighlights[] with criterion-level scoring
// - Marks synthetic scorecard evaluations with a dedicated voteSource
// - Keeps timestamps and demo-seed metadata consistent
// - Fully block-commented for clarity and future devs

import {
  serverTimestamp,
} from "firebase/firestore";

/**
 * ------------------------------------------------------------
 * BASE DEMO VOTE DOCUMENT
 * ------------------------------------------------------------
 * Shared human-readable and compatibility fields used by both
 * existing demo votes and Detailed Scorecard demo evaluations.
 */
const buildBaseDemoVoteDoc = ({
  generatedVote,
  eventId,
} = {}) => ({
  // =========================================================
  // Current / legacy LGV vote identity fields
  // =========================================================

  uid:
    generatedVote.voterId,

  eventId,

  voterName:
    generatedVote.voterName,

  cid:
    generatedVote.entryId,

  catid:
    generatedVote.categoryId,

  // =========================================================
  // Human-readable duplicate fields
  // =========================================================

  voterId:
    generatedVote.voterId,

  entryId:
    generatedVote.entryId,

  entryTitle:
    generatedVote.entryTitle,

  categoryId:
    generatedVote.categoryId,

  categoryName:
    generatedVote.categoryName,

  ballotPosition:
    generatedVote.ballotPosition ||
    1,

  demoSeed:
    true,

  source:
    "seed_engine",
});

/**
 * ------------------------------------------------------------
 * STANDARD DEMO VOTE
 * ------------------------------------------------------------
 * This preserves the original non-scorecard seed document shape.
 */
export const buildDemoVoteDoc = ({
  generatedVote,
  eventId,
} = {}) => ({
  ...buildBaseDemoVoteDoc({
    generatedVote,
    eventId,
  }),

  createdAt:
    serverTimestamp(),

  updatedAt:
    serverTimestamp(),
});

/**
 * ------------------------------------------------------------
 * DETAILED SCORECARD DEMO VOTE
 * ------------------------------------------------------------
 * Mirrors the authoritative fields persisted by production
 * submitVote for votingType === "points".
 *
 * Demo Builder writes these directly because synthetic demo users
 * are generated Firestore identities, not authenticated Firebase
 * Auth users capable of independently calling submitVote.
 */
export const buildDetailedScorecardDemoVoteDoc = ({
  generatedVote,
  eventId,
} = {}) => ({
  ...buildBaseDemoVoteDoc({
    generatedVote,
    eventId,
  }),

  votingType:
    "points",

  totalPoints:
    Number(
      generatedVote.totalPoints ||
      0
    ),

  maxBallotPoints:
    Number(
      generatedVote.maxBallotPoints ||
      100
    ),

  scores:
    Array.isArray(
      generatedVote.scores
    )
      ? generatedVote.scores
      : [],

  voteSource:
    "experience_factory_detailed_scorecard_seed",

  evaluationIndex:
    Number(
      generatedVote.evaluationIndex ||
      0
    ),

  createdAt:
    serverTimestamp(),

  updatedAt:
    serverTimestamp(),
});